import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/format";
import type { EaDetailPayload, EaIndicatorRow, EaSummary } from "../lib/types";
import { Card, CardTitle, Pill } from "../components/Card";

type AccountTab = "all" | "usd" | "cent";

export default function EasPage() {
  const [tab, setTab] = useState<AccountTab>("all");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [fireOpen, setFireOpen] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [closeAllOpen, setCloseAllOpen] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<
    { eaId: string; indicatorType: string } | null
  >(null);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["eas"],
    queryFn: () =>
      api.get<{
        eas: EaSummary[];
        marketPriceXau: { price: number; ts: string } | null;
      }>("/admin/api/eas"),
    // EAs heartbeat to the server every ~5s. Poll faster (3s) so the
    // table catches a fresh beat within one cycle. Background refetch
    // lets traders watch the panel alongside MT5 without tabbing back.
    refetchInterval: 3_000,
    refetchIntervalInBackground: true,
  });

  const closeMutation = useMutation({
    mutationFn: (body: { eaId?: string }) =>
      api.post<{ ok: boolean; queued: number; eaIds: string[] }>(
        "/admin/api/positions/close",
        body,
      ),
    onSuccess: () => {
      setCloseConfirm(null);
      void qc.invalidateQueries({ queryKey: ["eas"] });
    },
  });

  // Compute before early returns to honor rules-of-hooks. `useMemo`
  // returns [] when data isn't loaded yet — cheap and avoids a conditional
  // hook call (which React would crash on).
  const eas = data?.eas ?? [];
  const isCent = (e: EaSummary): boolean => (e.currency ?? "").toUpperCase() === "USC";
  const filtered = useMemo(() => {
    if (tab === "usd") return eas.filter((e) => !isCent(e));
    if (tab === "cent") return eas.filter(isCent);
    return eas;
  }, [eas, tab]);
  const counts = {
    all: eas.length,
    usd: eas.filter((e) => !isCent(e)).length,
    cent: eas.filter(isCent).length,
  };

  if (isLoading) return <div className="text-muted">Loading…</div>;
  if (error || !data) {
    return (
      <div className="rounded-md border border-trading-down/40 bg-trading-down/10 px-4 py-3 text-trading-down">
        Failed: {(error as Error)?.message}
      </div>
    );
  }
  const live = filtered.filter((e) => e.staleSeconds <= 90).length;
  const balanceSum = filtered.reduce((s, e) => s + e.balanceUsd, 0);
  const equitySum = filtered.reduce((s, e) => s + e.equityUsd, 0);
  const pnlSum = filtered.reduce((s, e) => s + e.pnlTodayUsd, 0);
  const realizedSum = filtered.reduce(
    (s, e) => s + (e.pnlRealizedTodayUsd ?? 0),
    0,
  );
  const buyLotsXauSum = filtered.reduce((s, e) => s + (e.buyLotsXau ?? 0), 0);
  const sellLotsXauSum = filtered.reduce((s, e) => s + (e.sellLotsXau ?? 0), 0);
  const buyLotsBtcSum = filtered.reduce((s, e) => s + (e.buyLotsBtc ?? 0), 0);
  const sellLotsBtcSum = filtered.reduce((s, e) => s + (e.sellLotsBtc ?? 0), 0);
  const netLotXau = buyLotsXauSum - sellLotsXauSum;
  const netLotBtc = buyLotsBtcSum - sellLotsBtcSum;
  // Floating = total P&L today minus realized today (sum of unrealized
  // across all EAs that report a realized number; falls back to pnlToday
  // when realized is null).
  const floatingSum = filtered.reduce(
    (s, e) =>
      s +
      (e.pnlRealizedTodayUsd == null
        ? 0
        : e.pnlTodayUsd - e.pnlRealizedTodayUsd),
    0,
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-on-dark sm:text-display-sm">Expert Advisors</h1>
          <p className="mt-1 text-sm text-muted">
            {filtered.length} EAs in view · {live} live · click to configure
            {data.marketPriceXau ? (
              <>
                {" · "}
                <span
                  className="font-mono text-warn"
                  title={`Broker XAU bid as of ${data.marketPriceXau.ts}`}
                >
                  XAU {data.marketPriceXau.price.toFixed(2)}
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-3 text-sm">
          <div className="min-w-[110px]">
            <div className="whitespace-nowrap text-[11px] uppercase tracking-wider text-muted">Total balance</div>
            <div className="font-mono text-lg text-on-dark">{fmtMoney(balanceSum)}</div>
          </div>
          <div className="min-w-[110px]">
            <div className="whitespace-nowrap text-[11px] uppercase tracking-wider text-muted">Total equity</div>
            <div className="font-mono text-lg text-on-dark">{fmtMoney(equitySum)}</div>
          </div>
          <div className="min-w-[110px]">
            <div className="whitespace-nowrap text-[11px] uppercase tracking-wider text-muted">Realized today</div>
            <div
              className={`font-mono text-lg ${realizedSum < 0 ? "text-trading-down" : realizedSum > 0 ? "text-trading-up" : "text-on-dark"}`}
            >
              {fmtMoney(realizedSum)}
            </div>
          </div>
          <div className="min-w-[110px]">
            <div className="whitespace-nowrap text-[11px] uppercase tracking-wider text-muted">Floating</div>
            <div
              className={`font-mono text-lg ${floatingSum < 0 ? "text-trading-down" : floatingSum > 0 ? "text-trading-up" : "text-on-dark"}`}
              title="Unrealized P&L (P&L today − Realized today)"
            >
              {fmtMoney(floatingSum)}
            </div>
          </div>
          <div className="min-w-[110px]">
            <div className="whitespace-nowrap text-[11px] uppercase tracking-wider text-muted">P&L today</div>
            <div
              className={`font-mono text-lg ${pnlSum < 0 ? "text-trading-down" : pnlSum > 0 ? "text-trading-up" : "text-on-dark"}`}
            >
              {fmtMoney(pnlSum)}
            </div>
          </div>
          <div className="min-w-[190px]">
            <div className="whitespace-nowrap text-[11px] uppercase tracking-wider text-muted">
              Net lot
            </div>
            <div className="mt-0.5 space-y-0.5">
              <div className="flex items-baseline gap-2 text-xs font-mono">
                <span className="w-7 text-[10px] text-muted">XAU</span>
                <span className="text-trading-up">{buyLotsXauSum.toFixed(2)}</span>
                <span className="text-muted">/</span>
                <span className="text-trading-down">{sellLotsXauSum.toFixed(2)}</span>
                <span
                  className={`ml-auto font-semibold ${netLotXau > 0 ? "text-trading-up" : netLotXau < 0 ? "text-trading-down" : "text-on-dark"}`}
                >
                  {netLotXau > 0 ? "+" : ""}
                  {netLotXau.toFixed(2)}
                </span>
              </div>
              <div className="flex items-baseline gap-2 text-xs font-mono">
                <span className="w-7 text-[10px] text-muted">BTC</span>
                <span className="text-trading-up">{buyLotsBtcSum.toFixed(2)}</span>
                <span className="text-muted">/</span>
                <span className="text-trading-down">{sellLotsBtcSum.toFixed(2)}</span>
                <span
                  className={`ml-auto font-semibold ${netLotBtc > 0 ? "text-trading-up" : netLotBtc < 0 ? "text-trading-down" : "text-on-dark"}`}
                >
                  {netLotBtc > 0 ? "+" : ""}
                  {netLotBtc.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1 rounded-md bg-surface-elevated p-0.5 text-sm w-fit">
          {(["all", "usd", "cent"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded px-3 py-1.5 transition ${
                tab === t
                  ? "bg-primary text-on-primary font-semibold"
                  : "text-muted hover:text-on-dark"
              }`}
            >
              {t === "all" ? "All accounts" : t === "usd" ? "USD" : "Cent (USC)"}
              <span className="ml-1.5 text-xs opacity-70">{counts[t]}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-md border border-trading-up/40 bg-trading-up/10 px-3 py-1.5 text-sm font-semibold text-trading-up hover:bg-trading-up/20"
            onClick={() => setFireOpen(true)}
            title="Manually fire a BUY / SELL / CLOSE signal — every EA with that indicator allowed picks it up on next 5s poll"
          >
            🎯 Fire signal…
          </button>
          <button
            type="button"
            className="rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary hover:bg-primary/20"
            onClick={() => setModifyOpen(true)}
            title="Change broker TP / SL pip distance on all open positions of one indicator across every EA"
          >
            ✏️ Modify TP/SL…
          </button>
          <button
            type="button"
            className="rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary hover:bg-primary/20"
            onClick={() => setBulkOpen(true)}
            title="Copy one EA's saved indicator config onto multiple EAs in 1 click"
          >
            Bulk indicator config…
          </button>
          <button
            type="button"
            className="rounded-md border border-trading-down/40 bg-trading-down/10 px-3 py-1.5 text-sm font-semibold text-trading-down hover:bg-trading-down/20"
            onClick={() => setCloseAllOpen(true)}
            title="Close every open position across every EA"
          >
            ✕ Close ALL
          </button>
        </div>
      </div>

      <Card>
        <CardTitle right="VNT timestamps">Live status</CardTitle>
        <div className="-mx-5 overflow-x-auto px-5">
        <table className="w-full min-w-[1180px] text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-muted">
            <tr>
              <th className="py-2">EA</th>
              <th>Indicators</th>
              <th>Cur</th>
              <th className="text-right">Balance (USD)</th>
              <th className="text-right">Equity</th>
              <th className="text-right">Realized</th>
              <th className="text-right">Target</th>
              <th className="text-right">Floating</th>
              <th className="text-right">P&L today</th>
              <th className="text-right">Open</th>
              <th className="text-right">Buy lot</th>
              <th className="text-right">Sell lot</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.eaId} className="border-t border-hairline-dark hover:bg-surface-elevated/40">
                <td className="py-2.5">
                  <Link
                    className="font-mono text-on-dark hover:text-primary"
                    to={`/eas/${encodeURIComponent(e.eaId)}`}
                  >
                    {e.eaId}
                  </Link>
                  <div className="text-[11px] text-muted">
                    <span className="font-mono">
                      {e.eaVersion ? `v${e.eaVersion}` : "—"}
                    </span>
                    <span className="mx-1">·</span>
                    <span className="font-mono">{e.accountId}</span>
                  </div>
                </td>
                <td>
                  {e.indicators.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {e.indicators.map((ind) => (
                        <button
                          key={ind}
                          type="button"
                          title={`Click to disable ${ind} on ${e.eaId}`}
                          onClick={() =>
                            setConfirmDisable({
                              eaId: e.eaId,
                              indicatorType: ind,
                            })
                          }
                          className="rounded-md border border-hairline-dark bg-surface-elevated px-2 py-0.5 text-[11px] font-mono text-on-dark hover:border-trading-down/50 hover:bg-trading-down/10 hover:text-trading-down"
                        >
                          {ind}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>
                  {e.currency ? (
                    <Pill tone={e.currency === "USC" ? "warn" : "neutral"}>{e.currency}</Pill>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="text-right font-mono">
                  {fmtMoney(e.balanceUsd)}
                  {e.currency === "USC" ? (
                    <span className="ml-1 text-xs text-muted">(¢{e.balance.toFixed(0)})</span>
                  ) : null}
                </td>
                <td className="text-right font-mono">{fmtMoney(e.equityUsd)}</td>
                <td className={`text-right font-mono ${e.pnlRealizedTodayUsd == null ? "text-muted" : e.pnlRealizedTodayUsd < 0 ? "text-trading-down" : e.pnlRealizedTodayUsd > 0 ? "text-trading-up" : "text-muted"}`}>
                  {e.pnlRealizedTodayUsd == null ? "—" : fmtMoney(e.pnlRealizedTodayUsd)}
                </td>
                <td className="text-right">
                  {(() => {
                    // Native-currency progress (D/W/M) so a cent EA with realized
                    // ¢250 against target ¢500 reads 50% — not 50% / factor.
                    // Same numbers the EA panel shows, from the latest heartbeat.
                    const bars = [
                      { k: "D", realized: e.pnlRealizedToday, target: e.dailyTarget },
                      { k: "W", realized: e.pnlRealizedWeek, target: e.weeklyTarget },
                      { k: "M", realized: e.pnlRealizedMonth, target: e.monthlyTarget },
                    ].filter((b) => b.target && b.target > 0);
                    if (bars.length === 0) {
                      return (
                        <span className="text-[10px] text-muted">no target</span>
                      );
                    }
                    return (
                      <div className="flex flex-col items-end gap-1">
                        {bars.map((b) => {
                          const realizedNative = b.realized == null ? 0 : b.realized;
                          const pct = (realizedNative / b.target) * 100;
                          const reached = pct >= 100;
                          const clamped = Math.max(0, Math.min(100, pct));
                          const tone = reached
                            ? "text-trading-up"
                            : pct < 0
                              ? "text-trading-down"
                              : "text-muted";
                          const barTone = reached
                            ? "bg-trading-up"
                            : pct < 0
                              ? "bg-trading-down"
                              : "bg-primary/70";
                          return (
                            <div
                              key={b.k}
                              className="flex items-center justify-end gap-1"
                              title={`${b.k}: ${realizedNative.toFixed(2)} / ${b.target.toFixed(2)} ${e.currency ?? ""}`}
                            >
                              <span className="w-3 text-[9px] text-muted">{b.k}</span>
                              <span className={`w-9 text-right text-[10px] font-mono ${tone}`}>
                                {Math.round(pct)}%{reached ? "✓" : ""}
                              </span>
                              <div className="h-1.5 w-16 overflow-hidden rounded bg-surface-elevated/60">
                                <div className={`h-full ${barTone}`} style={{ width: `${clamped}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </td>
                <td
                  className={`text-right font-mono ${
                    (() => {
                      const f = e.pnlRealizedTodayUsd == null ? 0 : e.pnlTodayUsd - e.pnlRealizedTodayUsd;
                      return f < 0 ? "text-trading-down" : f > 0 ? "text-trading-up" : "text-muted";
                    })()
                  }`}
                  title="Unrealized P&L from currently-open positions (P&L today − realized today)"
                >
                  {e.pnlRealizedTodayUsd == null
                    ? "—"
                    : fmtMoney(e.pnlTodayUsd - e.pnlRealizedTodayUsd)}
                </td>
                <td className={`text-right font-mono ${e.pnlTodayUsd < 0 ? "text-trading-down" : e.pnlTodayUsd > 0 ? "text-trading-up" : "text-muted"}`}>
                  {fmtMoney(e.pnlTodayUsd)}
                </td>
                <td className="text-right font-mono">{e.openPositions}</td>
                <td
                  className="text-right font-mono"
                  title={`XAU ${e.buyLotsXau.toFixed(2)} + BTC ${e.buyLotsBtc.toFixed(2)}`}
                >
                  <div className={e.buyLotsXau > 0 ? "text-trading-up" : "text-muted"}>
                    <span className="text-[10px] mr-1 text-muted">XAU</span>
                    {e.buyLotsXau.toFixed(2)}
                  </div>
                  <div className={`text-[11px] ${e.buyLotsBtc > 0 ? "text-trading-up" : "text-muted"}`}>
                    <span className="text-[10px] mr-1 text-muted">BTC</span>
                    {e.buyLotsBtc.toFixed(2)}
                  </div>
                </td>
                <td
                  className="text-right font-mono"
                  title={`XAU ${e.sellLotsXau.toFixed(2)} + BTC ${e.sellLotsBtc.toFixed(2)}`}
                >
                  <div className={e.sellLotsXau > 0 ? "text-trading-down" : "text-muted"}>
                    <span className="text-[10px] mr-1 text-muted">XAU</span>
                    {e.sellLotsXau.toFixed(2)}
                  </div>
                  <div className={`text-[11px] ${e.sellLotsBtc > 0 ? "text-trading-down" : "text-muted"}`}>
                    <span className="text-[10px] mr-1 text-muted">BTC</span>
                    {e.sellLotsBtc.toFixed(2)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      {bulkOpen ? (
        <BulkIndicatorConfigDialog
          eas={eas.map((e) => e.eaId)}
          onClose={() => setBulkOpen(false)}
        />
      ) : null}
      {confirmDisable ? (
        <QuickDisableDialog
          eaId={confirmDisable.eaId}
          indicatorType={confirmDisable.indicatorType}
          onClose={() => setConfirmDisable(null)}
          onDone={() => {
            setConfirmDisable(null);
            void qc.invalidateQueries({ queryKey: ["eas"] });
          }}
        />
      ) : null}
      {fireOpen ? (
        <FireSignalDialog
          onClose={() => setFireOpen(false)}
          onFired={() => {
            void qc.invalidateQueries({ queryKey: ["eas"] });
            void qc.invalidateQueries({ queryKey: ["positions"] });
          }}
        />
      ) : null}
      {modifyOpen ? (
        <ModifyTpSlDialog
          onClose={() => setModifyOpen(false)}
          onApplied={() => {
            void qc.invalidateQueries({ queryKey: ["eas"] });
            void qc.invalidateQueries({ queryKey: ["positions"] });
          }}
        />
      ) : null}
      {closeAllOpen ? (
        <CloseAllDialog
          totalOpen={filtered.reduce((s, e) => s + e.openPositions, 0)}
          eaCount={filtered.filter((e) => e.openPositions > 0).length}
          onClose={() => setCloseAllOpen(false)}
          onDone={() => {
            setCloseAllOpen(false);
            void qc.invalidateQueries({ queryKey: ["eas"] });
            void qc.invalidateQueries({ queryKey: ["positions"] });
          }}
        />
      ) : null}
      {closeConfirm ? (
        <ClosePositionsDialog
          eaId={closeConfirm}
          pending={closeMutation.isPending}
          error={closeMutation.error ? (closeMutation.error as Error).message : null}
          onCancel={() => setCloseConfirm(null)}
          onConfirm={() => closeMutation.mutate({ eaId: closeConfirm })}
        />
      ) : null}
    </div>
  );
}

// Per-row "close all open positions on this EA" confirm. Uses the same
// /admin/api/positions/close endpoint as the Positions page (eaId-scoped
// variant). Confirms because closing is irreversible — broker fills at
// market and the position is gone.
function ClosePositionsDialog({
  eaId,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  eaId: string;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-lg border border-hairline-dark bg-canvas-dark p-5">
        <div className="text-base font-semibold text-on-dark">
          Close all positions on <span className="font-mono">{eaId}</span>?
        </div>
        <p className="mt-2 text-sm text-muted">
          Queues a close command. EA picks it up on next 5s poll and closes
          every open position on this account at market price. This is
          irreversible — broker fills at whatever the live price is.
        </p>
        {error ? (
          <div className="mt-3 text-sm text-trading-down">{error}</div>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-hairline-dark px-3 py-2 text-sm text-muted hover:text-on-dark"
            onClick={onCancel}
            disabled={pending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-trading-down/90 px-3 py-2 text-sm font-semibold text-on-primary hover:bg-trading-down disabled:opacity-50"
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? "Closing…" : "Close all"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Global "close every open position on every EA" — destructive enough
// that we double-confirm. Calls /admin/api/positions/close with an empty
// body so the existing endpoint fans out to every EA that has open
// positions (no eaId / indicator filter).
function CloseAllDialog({
  totalOpen,
  eaCount,
  onClose,
  onDone,
}: {
  totalOpen: number;
  eaCount: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const mut = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; queued: number; eaIds: string[] }>(
        "/admin/api/positions/close",
        {},
      ),
    onSuccess: () => onDone(),
    onError: (err) => setError((err as Error).message),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-lg border border-trading-down/40 bg-canvas-dark p-5">
        <div className="text-base font-semibold text-trading-down">
          Close ALL open positions?
        </div>
        <p className="mt-2 text-sm text-muted">
          Queues a close command on every EA that has open positions.
          {" "}
          <span className="font-mono text-on-dark">{totalOpen}</span> position(s)
          across{" "}
          <span className="font-mono text-on-dark">{eaCount}</span> EA(s) will
          close at market on next poll (~2s). Irreversible — broker fills at
          whatever the live price is.
        </p>
        <label className="mt-4 flex items-start gap-2 text-sm text-on-dark">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <span>
            I understand this closes every position across every EA right now.
          </span>
        </label>
        {error ? (
          <div className="mt-3 text-sm text-trading-down">{error}</div>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-hairline-dark px-3 py-2 text-sm text-muted hover:text-on-dark"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-trading-down/90 px-3 py-2 text-sm font-semibold text-on-primary hover:bg-trading-down disabled:opacity-50"
            disabled={!confirmed || mut.isPending || totalOpen === 0}
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
          >
            {mut.isPending
              ? "Closing…"
              : `Close ${totalOpen} position${totalOpen === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Manual signal firing — same shape as the dialog on Positions page.
// Inlined here so EAs list has its own copy without page-cross imports.
type FireAction = "BUY" | "SELL" | "CLOSE_BUY" | "CLOSE_SELL";
function FireSignalDialog({
  onClose,
  onFired,
}: {
  onClose: () => void;
  onFired: () => void;
}) {
  const [indicator, setIndicator] = useState("TT-WH");
  const [action, setAction] = useState<FireAction>("BUY");
  const [symbol, setSymbol] = useState<"XAUUSD" | "BTCUSD">("XAUUSD");
  const [error, setError] = useState<string | null>(null);

  const known = useQuery({
    queryKey: ["indicators-known"],
    queryFn: () => api.get<{ types: string[] }>("/admin/api/indicators/known"),
  });

  // Sync state with the live list — if the seeded default isn't in the
  // known set (e.g. we retired it server-side), fall through to whatever
  // the dropdown will actually render. Without this the state stays on a
  // value that has no matching <option>, so the form posts the stale name
  // even though the UI looks correct.
  useEffect(() => {
    const types = known.data?.types ?? [];
    if (types.length > 0 && !types.includes(indicator)) {
      setIndicator(types[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(known.data?.types)]);

  const fireMut = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; signalId: number; deduped: boolean }>(
        "/admin/api/signals/fire",
        { indicatorType: indicator, action, symbol },
      ),
    onSuccess: (res) => {
      if (res.deduped) {
        setError("Signal deduped — already fired recently / kind mismatch.");
      } else {
        onFired();
        onClose();
      }
    },
    onError: (err) => setError((err as Error).message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-lg border border-hairline-dark bg-canvas-dark p-5">
        <div className="text-base font-semibold text-on-dark">Fire signal manually</div>
        <p className="mt-1 text-sm text-muted">
          Inserts a fresh signal as if Pine had fired it. Every EA with this
          indicator allowed will open / close a position on its next 5s poll.
        </p>

        <label className="mt-4 block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
            Indicator
          </span>
          <select
            className="w-full rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 font-mono text-on-dark"
            value={indicator}
            onChange={(e) => setIndicator(e.target.value)}
          >
            {(known.data?.types ?? ["TT-WH", "ISB-live", "macd-long"]).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>

        <div className="mt-3">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-muted">Symbol</div>
          <div className="flex gap-2">
            {(["XAUUSD", "BTCUSD"] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={`flex-1 rounded-md border px-3 py-1.5 text-sm font-semibold ${
                  symbol === s
                    ? "border-primary bg-primary/20 text-primary"
                    : "border-hairline-dark text-muted hover:text-on-dark"
                }`}
                onClick={() => setSymbol(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-muted">Action</div>
          <div className="grid grid-cols-2 gap-2">
            {([
              { key: "BUY" as const, label: "🟢 OPEN BUY", tone: "up" },
              { key: "SELL" as const, label: "🔴 OPEN SELL", tone: "down" },
              { key: "CLOSE_BUY" as const, label: "⚪ CLOSE BUY", tone: "up" },
              { key: "CLOSE_SELL" as const, label: "⚪ CLOSE SELL", tone: "down" },
            ]).map((a) => (
              <button
                key={a.key}
                type="button"
                className={`rounded-md border px-3 py-2 text-sm font-semibold ${
                  action === a.key
                    ? a.tone === "up"
                      ? "border-trading-up bg-trading-up/20 text-trading-up"
                      : "border-trading-down bg-trading-down/20 text-trading-down"
                    : "border-hairline-dark text-muted hover:text-on-dark"
                }`}
                onClick={() => setAction(a.key)}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <div className="mt-3 rounded-md border border-trading-down/40 bg-trading-down/10 px-3 py-2 text-sm text-trading-down">
            {error}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-hairline-dark px-4 py-2 text-sm text-on-dark"
            onClick={onClose}
            disabled={fireMut.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`rounded-md px-4 py-2 text-sm font-semibold text-on-primary disabled:opacity-50 ${
              action === "BUY" || action === "CLOSE_BUY"
                ? "bg-trading-up/90 hover:bg-trading-up"
                : "bg-trading-down/90 hover:bg-trading-down"
            }`}
            onClick={() => {
              setError(null);
              fireMut.mutate();
            }}
            disabled={fireMut.isPending}
          >
            {fireMut.isPending ? "Firing…" : `Fire ${action} ${indicator} ${symbol}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// One-click "disable indicator X on EA Y" — hits the lightweight
// /allowed endpoint that only flips the `allowed` column (preserves lot,
// closeMode, TP/SL, dyn-TP). Re-enable by going to the EA detail page.
function QuickDisableDialog({
  eaId,
  indicatorType,
  onClose,
  onDone,
}: {
  eaId: string;
  indicatorType: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.post(
        `/admin/api/eas/${encodeURIComponent(eaId)}/indicators/${encodeURIComponent(indicatorType)}/allowed`,
        { allowed: false },
      ),
    onSuccess: () => onDone(),
    onError: (err) => setError((err as Error).message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-lg border border-hairline-dark bg-canvas-dark p-5">
        <div className="text-base font-semibold text-on-dark">
          Disable <span className="font-mono">{indicatorType}</span> on{" "}
          <span className="font-mono">{eaId}</span>?
        </div>
        <p className="mt-2 text-sm text-muted">
          EA will stop receiving new signals from this indicator on the next
          5s poll. Existing open positions stay open — broker SL/TP or a
          manual close still works. Other config (lot, close mode, TP/SL
          pips) is preserved so re-enable on the EA detail page restores it.
        </p>
        {error ? (
          <div className="mt-3 text-sm text-trading-down">{error}</div>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-hairline-dark px-3 py-2 text-sm text-muted hover:text-on-dark"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-trading-down/90 px-3 py-2 text-sm font-semibold text-on-primary hover:bg-trading-down disabled:opacity-50"
            disabled={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            {mutation.isPending ? "Disabling…" : "Disable"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Wizard: pick a source EA → pick one of its allowed indicators → pick
// target EAs → preview the config that will be copied → Apply. Calls the
// same `/admin/api/eas/:eaId/indicators/:indicatorType/apply-to` endpoint
// used by the per-indicator button on the EA detail page.
function BulkIndicatorConfigDialog({
  eas,
  onClose,
}: {
  eas: string[];
  onClose: () => void;
}) {
  const sortedEas = useMemo(() => [...eas].sort(), [eas]);
  const [sourceEa, setSourceEa] = useState<string>(sortedEas[0] ?? "");
  const [indicator, setIndicator] = useState<string>("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [doneMsg, setDoneMsg] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ["ea-detail-for-bulk", sourceEa],
    queryFn: () =>
      api.get<EaDetailPayload>(`/admin/api/eas/${encodeURIComponent(sourceEa)}`),
    enabled: !!sourceEa,
  });

  // Only offer indicators that the source EA has explicitly configured AND
  // marked allowed — copying a blocked / never-touched row is rarely what
  // the admin wants, and would silently zero out the target EA.
  const sourceRows: EaIndicatorRow[] = detailQuery.data?.indicators ?? [];
  const allowedIndicators = sourceRows
    .filter((r) => r.allowed)
    .map((r) => r.indicatorType)
    .sort();

  // Auto-pick the first allowed indicator when source changes / data loads.
  useEffect(() => {
    if (allowedIndicators.length === 0) {
      if (indicator !== "") setIndicator("");
      return;
    }
    if (!allowedIndicators.includes(indicator)) {
      setIndicator(allowedIndicators[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allowedIndicators)]);

  const previewRow = sourceRows.find((r) => r.indicatorType === indicator) ?? null;
  const targetEas = sortedEas.filter((id) => id !== sourceEa);
  const allChecked = targetEas.length > 0 && targetEas.every((id) => selected[id]);
  const toggleAll = (on: boolean) => {
    const next: Record<string, boolean> = {};
    for (const id of targetEas) next[id] = on;
    setSelected(next);
  };

  const mutation = useMutation({
    mutationFn: () => {
      const targetEaIds = targetEas.filter((id) => selected[id]);
      return api.post<{ ok: boolean; written: number }>(
        `/admin/api/eas/${encodeURIComponent(sourceEa)}/indicators/${encodeURIComponent(indicator)}/apply-to`,
        { targetEaIds },
      );
    },
    onSuccess: (resp) => {
      setError(null);
      setDoneMsg(`Applied to ${resp.written} EA${resp.written === 1 ? "" : "s"}.`);
    },
    onError: (err) => {
      setDoneMsg(null);
      setError((err as Error).message);
    },
  });

  const selectedCount = targetEas.filter((id) => selected[id]).length;
  const canApply =
    !!sourceEa && !!indicator && selectedCount > 0 && !mutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-2xl rounded-lg border border-hairline-dark bg-canvas-dark p-5">
        <div className="text-base font-semibold text-on-dark">
          Bulk indicator config
        </div>
        <p className="mt-1 text-sm text-muted">
          Copy the SAVED config of one indicator from a source EA onto multiple
          target EAs in one click. Source EA's unsaved local edits are NOT
          included — open the EA detail page and Save first if you want them.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
              Source EA
            </span>
            <select
              className="w-full rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 font-mono text-on-dark"
              value={sourceEa}
              onChange={(e) => {
                setSourceEa(e.target.value);
                setIndicator("");
                setDoneMsg(null);
              }}
            >
              {sortedEas.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
              Indicator (allowed on source)
            </span>
            <select
              className="w-full rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 font-mono text-on-dark disabled:opacity-60"
              value={indicator}
              onChange={(e) => {
                setIndicator(e.target.value);
                setDoneMsg(null);
              }}
              disabled={detailQuery.isLoading || allowedIndicators.length === 0}
            >
              {allowedIndicators.length === 0 ? (
                <option value="">No allowed indicators on this EA</option>
              ) : (
                allowedIndicators.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))
              )}
            </select>
          </label>
        </div>

        {previewRow ? (
          <div className="mt-4 rounded-md border border-hairline-dark bg-surface-elevated/40 p-3">
            <div className="mb-2 text-[11px] uppercase tracking-wider text-muted">
              Config preview · will be copied
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
              <PreviewField label="lot" value={previewRow.lot ?? "preset"} />
              <PreviewField label="closeMode" value={previewRow.closeMode} />
              <PreviewField
                label="closeOnAlert"
                value={previewRow.closeOnAlert ? "on" : "off"}
              />
              <PreviewField label="TP pip" value={previewRow.tpPips ?? "off"} />
              <PreviewField label="SL pip" value={previewRow.slPips ?? "off"} />
              <PreviewField
                label="closeOnSl"
                value={previewRow.closeOnSl ? "on" : "off"}
              />
              <PreviewField
                label="default TP"
                value={previewRow.closeOnTp == null ? "none" : `TP${previewRow.closeOnTp}`}
              />
              <PreviewField
                label="min SL dist"
                value={previewRow.minSlDistance ?? "off"}
              />
              <PreviewField
                label="dyn TP (1/2/3)"
                value={`${previewRow.dynTpSl1 ?? "—"}/${previewRow.dynTpSl2 ?? "—"}/${previewRow.dynTpSl3 ?? "—"}`}
              />
            </div>
          </div>
        ) : detailQuery.isLoading ? (
          <div className="mt-4 text-sm text-muted">Loading source config…</div>
        ) : null}

        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-muted">
              Target EAs
            </span>
            <div className="flex items-center gap-4 text-xs">
              <label className="flex items-center gap-2 text-muted">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
                <span>Select all ({targetEas.length})</span>
              </label>
              <span className="text-muted">{selectedCount} selected</span>
            </div>
          </div>
          {targetEas.length === 0 ? (
            <div className="rounded-md border border-hairline-dark p-3 text-sm text-muted">
              No other EAs available.
            </div>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-md border border-hairline-dark p-2">
              {targetEas.map((id) => (
                <label
                  key={id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-surface-elevated"
                >
                  <input
                    type="checkbox"
                    checked={!!selected[id]}
                    onChange={(e) =>
                      setSelected((st) => ({ ...st, [id]: e.target.checked }))
                    }
                  />
                  <span className="font-mono text-on-dark">{id}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {error ? (
          <div className="mt-3 text-sm text-trading-down">{error}</div>
        ) : null}
        {doneMsg ? (
          <div className="mt-3 text-sm text-trading-up">{doneMsg}</div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-hairline-dark px-3 py-2 text-sm text-muted hover:text-on-dark"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Close
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!canApply}
            onClick={() => {
              setError(null);
              setDoneMsg(null);
              mutation.mutate();
            }}
          >
            {mutation.isPending
              ? "Applying…"
              : `Apply to ${selectedCount} EA${selectedCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewField({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted">{label}</span>
      <span className="font-mono text-on-dark">{String(value)}</span>
    </div>
  );
}

// Broadcast modify TP/SL command to every EA holding a position of the
// chosen indicator. Same shape as the dialog on Positions page — inlined
// here so EAs list has its own copy without page-cross imports. Fetches
// /admin/api/positions to derive the indicator → open-count list.
function ModifyTpSlDialog({
  onClose,
  onApplied,
}: {
  onClose: () => void;
  onApplied: () => void;
}) {
  const positionsQuery = useQuery({
    queryKey: ["positions-for-modify"],
    queryFn: () =>
      api.get<{
        positions: Array<{ eaId: string; comment: string | null; side: string }>;
      }>("/admin/api/positions"),
  });
  // Derive indicator list with open count from current positions.
  const indicatorEntries = (() => {
    const counts = new Map<string, number>();
    for (const p of positionsQuery.data?.positions ?? []) {
      const m = (p.comment ?? "").match(/^TV@([^@]+)@S/);
      if (!m) continue;
      counts.set(m[1], (counts.get(m[1]) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([indicator, count]) => ({ indicator, count }))
      .sort((a, b) => a.indicator.localeCompare(b.indicator));
  })();

  const [indicator, setIndicator] = useState("");
  const [side, setSide] = useState<"buy" | "sell" | "both">("both");
  const [tpPips, setTpPips] = useState("");
  const [slPips, setSlPips] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!indicator && indicatorEntries.length > 0) {
      setIndicator(indicatorEntries[0].indicator);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicatorEntries.length]);

  const mut = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; queued: number; eaIds: string[]; note?: string }>(
        "/admin/api/positions/modify",
        {
          indicatorType: indicator,
          tpPips: tpPips ? Math.floor(Number(tpPips)) : null,
          slPips: slPips ? Math.floor(Number(slPips)) : null,
          side: side === "both" ? null : side,
        },
      ),
    onSuccess: (res) => {
      if (res.queued === 0) {
        setError(res.note ?? "No matching positions.");
      } else {
        onApplied();
        onClose();
      }
    },
    onError: (err) => setError((err as Error).message),
  });

  const tpN = tpPips ? Number(tpPips) : null;
  const slN = slPips ? Number(slPips) : null;
  const canApply =
    !!indicator &&
    !mut.isPending &&
    (tpN !== null || slN !== null) &&
    (tpN === null || (Number.isInteger(tpN) && tpN >= 1 && tpN <= 10000)) &&
    (slN === null || (Number.isInteger(slN) && slN >= 1 && slN <= 10000));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-lg border border-hairline-dark bg-canvas-dark p-5">
        <div className="text-base font-semibold text-on-dark">Modify TP / SL</div>
        <p className="mt-1 text-sm text-muted">
          Updates broker TP / SL pip distance on every currently-open position
          of the chosen indicator across all EAs. Pip distance is measured
          from each position's open price (EA applies side-aware sign). Leave
          a field empty to keep that side unchanged.
        </p>

        <label className="mt-4 block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
            Indicator
          </span>
          {positionsQuery.isLoading ? (
            <div className="rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 text-sm text-muted">
              Loading positions…
            </div>
          ) : indicatorEntries.length === 0 ? (
            <div className="rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 text-sm text-muted">
              No open positions to modify.
            </div>
          ) : (
            <select
              className="w-full rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 font-mono text-on-dark"
              value={indicator}
              onChange={(e) => setIndicator(e.target.value)}
            >
              {indicatorEntries.map((e) => (
                <option key={e.indicator} value={e.indicator}>
                  {e.indicator} ({e.count} open)
                </option>
              ))}
            </select>
          )}
        </label>

        <div className="mt-3">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-muted">Side</div>
          <div className="flex gap-2">
            {(["both", "buy", "sell"] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={`flex-1 rounded-md border px-3 py-1.5 text-sm font-semibold ${
                  side === s
                    ? s === "buy"
                      ? "border-trading-up bg-trading-up/20 text-trading-up"
                      : s === "sell"
                        ? "border-trading-down bg-trading-down/20 text-trading-down"
                        : "border-primary bg-primary/20 text-primary"
                    : "border-hairline-dark text-muted hover:text-on-dark"
                }`}
                onClick={() => setSide(s)}
              >
                {s === "both" ? "BUY + SELL" : s === "buy" ? "🟢 BUY" : "🔴 SELL"}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
              TP pip
            </span>
            <input
              type="number"
              min={1}
              max={10000}
              step={1}
              placeholder="leave blank = keep"
              className="w-full rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 font-mono"
              value={tpPips}
              onChange={(e) => setTpPips(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
              SL pip
            </span>
            <input
              type="number"
              min={1}
              max={10000}
              step={1}
              placeholder="leave blank = keep"
              className="w-full rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 font-mono"
              value={slPips}
              onChange={(e) => setSlPips(e.target.value)}
            />
          </label>
        </div>

        {error ? (
          <div className="mt-3 rounded-md border border-trading-down/40 bg-trading-down/10 px-3 py-2 text-sm text-trading-down">
            {error}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-hairline-dark px-4 py-2 text-sm text-on-dark"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!canApply}
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
          >
            {mut.isPending ? "Applying…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
